import React, { Suspense } from "react";
import styled from "styled-components/macro";
import { useFlags } from "launchdarkly-react-client-sdk";

import AccountIndicator, { LoadingAccountIndicator } from "ui/AccountIndicator";
import ErrorBoundary from "lib/ErrorBoundary";
import Suspender from "lib/Suspender";
import createAbsolutePath from "createAbsolutePath";
import { ActiveProfileQuery } from "operations";
import { useActiveProfileQuery } from "graphql/queries/ActiveProfile";

import NavBarAccountDropdown from "./NavBarAccountDropdown";
import NavBarItemImageLink from "./NavBarItemImageLink";
import NavBarMessageLink from "./NavBarMessageLink";
import NavBarProfileDropdown from "./NavBarProfileDropdown";
import { ReactComponent as JoorLogo } from "./JoorLogo.svg";
import NavBarItemTextLink from "./NavBarItemTextLink";
import NavBarShopDropdown from "./NavBarShopDropdown";
import NavbarRevamp from "./NavbarRevamp/NavbarRevamp.component";

const StyledHeader = styled.header`
  top: 0;
  position: relative;
  z-index: 10000;
  height: 53px;
  min-height: 50px;
  display: flex;
  align-items: center;
  background-color: var(--color-primary-800);
  color: white;
`;

const LeftQueue = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const RightQueue = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-left: auto;
`;

const Header = (props: ActiveProfileQuery) => {
  const { workspaceListPage, earlyAccessProRetailersNavbar } = useFlags();

  if (earlyAccessProRetailersNavbar) {
    return <NavbarRevamp {...props} />;
  }

  return (
    <StyledHeader>
      <AccountIndicator {...props} />
      <LeftQueue>
        <NavBarItemImageLink
          image={<JoorLogo />}
          route={createAbsolutePath("/accounts/home")}
        />
        <NavBarShopDropdown />
        {workspaceListPage && (
          <NavBarItemTextLink
            value="Workspaces"
            route={createAbsolutePath("/ra/workspaces")}
          />
        )}
        {!workspaceListPage && (
          <NavBarItemTextLink
            value="Assortment"
            route={createAbsolutePath("/assortments")}
          />
        )}
        <NavBarMessageLink />
        <NavBarItemTextLink
          value="Reporting"
          route={createAbsolutePath("/reports/retail_sales")}
        />
      </LeftQueue>
      <RightQueue>
        <NavBarProfileDropdown {...props} />
        <NavBarAccountDropdown {...props} />
      </RightQueue>
    </StyledHeader>
  );
};

const LoadingHeader: React.FC = () => (
  <StyledHeader>
    <LoadingAccountIndicator />
    <LeftQueue>
      <NavBarItemImageLink
        image={<JoorLogo />}
        route={createAbsolutePath("/accounts/home")}
      />
    </LeftQueue>
  </StyledHeader>
);

export const HeaderContainer = () => {
  const queryResponse = useActiveProfileQuery({
    decodeNodeIds: true,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });
  return (
    <ErrorBoundary FallbackComponent={() => <LoadingHeader />}>
      <Suspense fallback={<LoadingHeader />}>
        <Suspender
          suspend={queryResponse.loading && queryResponse.data === undefined}
        >
          {queryResponse.data && <Header {...queryResponse.data} />}
        </Suspender>
      </Suspense>
    </ErrorBoundary>
  );
};
